import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getPost, linkImagesToPost, type PostWithRelations, type Platform } from '../../lib/api';
import GenerateImagesModal from '../../components/image-generation/GenerateImagesModal';
import ImageJobProgress from '../../components/image-generation/ImageJobProgress';
import ImageVariantsGrid from '../../components/image-generation/ImageVariantsGrid';

/**
 * Generate AI images for a post
 * Shows modal to configure generation, then progress, then variants grid
 */
export default function GenerateImagesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug, postId } = useParams<{ workspaceSlug: string; postId: string }>();
  const location = useLocation();

  // Get job ID from location state (if redirected from modal)
  const prefilledJobId = location.state?.jobId;

  // State
  const [post, setPost] = useState<PostWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(!prefilledJobId);
  const [currentJobId, setCurrentJobId] = useState<string | null>(prefilledJobId || null);
  const [jobStatus, setJobStatus] = useState<any>(null);

  // Load post on mount
  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId]);

  const loadPost = async () => {
    if (!postId) return;

    setIsLoading(true);
    try {
      const postData = await getPost(postId);
      setPost(postData);
    } catch (error) {
      console.error('Failed to load post:', error);
      toast.error(t('imageGeneration.errors.noPost'));
      navigate(`/${workspaceSlug}/approve`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobStarted = (jobId: string) => {
    setCurrentJobId(jobId);
    setShowModal(false);
  };

  const handleJobComplete = (status: any) => {
    setJobStatus(status);
    setCurrentJobId(null);
  };

  const handleJobError = (error: string) => {
    toast.error(error);
    setCurrentJobId(null);
    setShowModal(true); // Allow retry
  };

  const handleLinkImages = async (assignments: Record<string, string>) => {
    if (!postId) return;

    try {
      await linkImagesToPost(postId, assignments);
      toast.success(t('imageGeneration.success.linked'));
      navigate(`/${workspaceSlug}/approve`);
    } catch (error: any) {
      console.error('Failed to link images:', error);
      toast.error(error.response?.data?.error || t('imageGeneration.errors.linkingFailed'));
    }
  };

  const getAvailablePlatforms = (): Platform[] => {
    if (!post?.variants) return [];
    return [...new Set(post.variants.map((v) => v.platform as Platform))];
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-black dark:border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">{t('imageGeneration.errors.noPost')}</p>
        </div>
      </div>
    );
  }

  const availablePlatforms = getAvailablePlatforms();

  return (
    <div className="max-w-6xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/${workspaceSlug}/approve`)}
          className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 flex items-center gap-2"
        >
          ← {t('common.back', { defaultValue: 'Back' })}
        </button>
        <h1 className="text-3xl font-bold dark:text-white mb-2">
          {t('imageGeneration.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {post.post.title || post.post.sourceText?.substring(0, 100) + '...'}
        </p>
      </div>

      {/* Content */}
      {currentJobId ? (
        /* Show job progress */
        <ImageJobProgress
          jobId={currentJobId}
          onComplete={handleJobComplete}
          onError={handleJobError}
        />
      ) : jobStatus && jobStatus.variants && jobStatus.variants.length > 0 ? (
        /* Show variants grid after completion */
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold dark:text-white mb-2">
              {t('imageGeneration.variants.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('imageGeneration.variants.assignImages')}
            </p>
          </div>

          <ImageVariantsGrid
            variants={jobStatus.variants}
            onVariantsUpdated={handleVariantsUpdated}
            showLinkButton={true}
            onLinkImages={handleLinkImages}
          />

          {/* Actions */}
          <div className="mt-8 flex justify-between items-center pt-6 border-t-2 border-gray-200 dark:border-gray-800">
            <button
              onClick={() => {
                setJobStatus(null);
                setShowModal(true);
              }}
              className="px-4 py-2 border-2 border-gray-300 dark:border-gray-700 hover:border-black dark:hover:border-white font-medium transition-colors"
            >
              {t('imageGeneration.generate')} {t('common.again', { defaultValue: 'again' })}
            </button>
            <button
              onClick={() => navigate(`/${workspaceSlug}/approve`)}
              className="px-4 py-2 border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white font-medium transition-colors"
            >
              {t('common.done', { defaultValue: 'Done' })}
            </button>
          </div>
        </div>
      ) : (
        /* Show empty state with button to open modal */
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t('imageGeneration.subtitle')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-3 border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white font-medium transition-colors"
          >
            {t('imageGeneration.generate')}
          </button>
        </div>
      )}

      {/* Generation Modal */}
      {showModal && postId && (
        <GenerateImagesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          postId={postId}
          availablePlatforms={availablePlatforms}
          onJobStarted={handleJobStarted}
        />
      )}
    </div>
  );
}
